import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import AppLayout from './components/AppLayout';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import './App.css';

// Lazy-loaded pages
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Reports = lazy(() => import('./pages/Reports'));
const CoinsPage = lazy(() => import('./pages/CoinsPage'));
const Patents = lazy(() => import('./pages/Patents'));
const WorkOrdersQueue = lazy(() => import('./pages/WorkOrdersQueue'));
const MyWorkOrders = lazy(() => import('./pages/MyWorkOrders'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const NewCall = lazy(() => import('./pages/NewCall'));
const NewReport = lazy(() => import('./pages/NewReport'));
const Departments = lazy(() => import('./pages/Departments'));
const DepartmentQueue = lazy(() => import('./pages/DepartmentQueue'));
const MaterialsPage = lazy(() => import('./pages/MaterialsPage'));
const BonusRulesPage = lazy(() => import('./pages/BonusRulesPage'));
const RedemptionsPage = lazy(() => import('./pages/RedemptionsPage'));
const TechDashboard = lazy(() => import('./pages/TechDashboard'));
const JobRolesPage = lazy(() => import('./pages/JobRolesPage'));
const PurchaseRequestsPage = lazy(() => import('./pages/PurchaseRequestsPage'));
const PurchaseRequestDetailPage = lazy(() => import('./pages/PurchaseRequestDetailPage'));
const StockMovementsPage = lazy(() => import('./pages/StockMovementsPage'));
const MyPerformancePage = lazy(() => import('./pages/MyPerformancePage'));
const SystemSettingsPage = lazy(() => import('./pages/SystemSettingsPage'));
const RolesPage = lazy(() => import('./pages/RolesPage'));
const WorkSchedulePage = lazy(() => import('./pages/WorkSchedulePage'));
const SuppliersPage = lazy(() => import('./pages/SuppliersPage'));
const MaterialCategoriesPage = lazy(() => import('./pages/MaterialCategoriesPage'));
const QuotationsPage = lazy(() => import('./pages/QuotationsPage'));
const QuotationDetailPage = lazy(() => import('./pages/QuotationDetailPage'));
const SupplierPortalPage = lazy(() => import('./pages/SupplierPortalPage'));
const PurchaseOrdersPage = lazy(() => import('./pages/PurchasingKanbanPage'));
const PurchaseOrderDetailPage = lazy(() => import('./pages/PurchaseOrderDetailPage'));
const StockAlertsPage = lazy(() => import('./pages/StockAlertsPage'));
const PlatformAdminPage = lazy(() => import('./pages/PlatformAdminPage'));
const CompanySettingsPage = lazy(() => import('./pages/CompanySettingsPage'));
const LocationConfigPage = lazy(() => import('./pages/LocationConfigPage'));
const MyReportsPage = lazy(() => import('./pages/MyReportsPage'));
const POConfirmationPage = lazy(() => import('./pages/POConfirmationPage'));
const PurchaseDashboardPage = lazy(() => import('./pages/PurchaseDashboardPage'));
const MaterialRequestsPage = lazy(() => import('./pages/MaterialRequestsPage'));
const MaterialReturnsPage = lazy(() => import('./pages/MaterialReturnsPage'));
const StockReceivingPage = lazy(() => import('./pages/StockReceivingPage'));
const PurchaseTrackingPage = lazy(() => import('./pages/PurchaseTrackingPage'));
const StockPurchaseReceivingPage = lazy(() => import('./pages/StockPurchaseReceivingPage'));
const StockPurchaseRequisitionPage = lazy(() => import('./pages/StockPurchaseRequisitionPage'));
const StockModulePage = lazy(() => import('./pages/StockModulePage'));
const SupplyRequestsPage = lazy(() => import('./pages/SupplyRequestsPage'));
const MaterialReleasePage = lazy(() => import('./pages/MaterialReleasePage'));
const MaterialRequestPage = lazy(() => import('./pages/MaterialRequestPage'));
const MyCallsPage = lazy(() => import('./pages/MyCallsPage'));
const ToolsPage = lazy(() => import('./pages/ToolsPage'));
const MessageTemplatesPage = lazy(() => import('./pages/MessageTemplatesPage'));
const WhatsAppLogsPage = lazy(() => import('./pages/WhatsAppLogsPage'));
const OperationalRoutinesPage = lazy(() => import('./pages/OperationalRoutinesPage'));
const CoinExtractPage = lazy(() => import('./pages/CoinExtractPage'));
const POShortageDashboardPage = lazy(() => import('./pages/POShortageDashboardPage'));
const TechniciansStatusPage = lazy(() => import('./pages/TechniciansStatusPage'));
const OSAnalyticsDrilldownPage = lazy(() => import('./pages/OSAnalyticsDrilldownPage'));

function TenantRoutes() {
  return (
    <TenantProvider>
      <Routes>
        <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="calls/new" element={<NewCall />} />
          <Route path="my-calls" element={<MyCallsPage />} />
          <Route path="reports/new" element={<NewReport />} />
          <Route path="reports/validate" element={<Reports />} />
          <Route path="reports" element={<Navigate to="reports/validate" replace />} />
          <Route path="work-orders" element={<WorkOrdersQueue />} />
          <Route path="my-work-orders" element={<MyWorkOrders />} />
          <Route path="departments" element={<Departments />} />
          <Route path="departments/queue" element={<DepartmentQueue />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="coins" element={<CoinsPage />} />
          <Route path="patents" element={<Patents />} />
          <Route path="materials" element={<MaterialsPage />} />
          <Route path="bonus-rules" element={<BonusRulesPage />} />
          <Route path="redemptions" element={<RedemptionsPage />} />
          <Route path="tech-dashboard" element={<TechDashboard />} />
          <Route path="job-roles" element={<JobRolesPage />} />
          <Route path="purchase-requests" element={<PurchaseRequestsPage />} />
          <Route path="purchase-requests/:id" element={<PurchaseRequestDetailPage />} />
          <Route path="material-requests" element={<MaterialRequestsPage />} />
          <Route path="material-returns" element={<MaterialReturnsPage />} />
          <Route path="stock-receiving" element={<StockReceivingPage />} />
          <Route path="stock-purchase-receiving" element={<StockPurchaseReceivingPage />} />
          <Route path="stock-purchase-requisition" element={<StockPurchaseRequisitionPage />} />
          <Route path="supply-requests" element={<StockModulePage />} />
          <Route path="material-releases" element={<MaterialReleasePage />} />
          <Route path="material-request" element={<MaterialRequestPage />} />
          <Route path="stock-movements" element={<StockMovementsPage />} />
          <Route path="my-performance" element={<MyPerformancePage />} />
          <Route path="system-settings" element={<SystemSettingsPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="work-schedule" element={<WorkSchedulePage />} />
          <Route path="suppliers" element={<SuppliersPage />} />
          <Route path="material-categories" element={<MaterialCategoriesPage />} />
          <Route path="quotations" element={<QuotationsPage />} />
          <Route path="quotations/:id" element={<QuotationDetailPage />} />
          <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
          <Route path="purchase-orders/:id" element={<PurchaseOrderDetailPage />} />
          <Route path="purchase-dashboard" element={<PurchaseDashboardPage />} />
          <Route path="purchase-tracking" element={<PurchaseTrackingPage />} />
          <Route path="stock-alerts" element={<StockAlertsPage />} />
          <Route path="company-settings" element={<CompanySettingsPage />} />
          <Route path="location-config" element={<LocationConfigPage />} />
          <Route path="my-reports" element={<MyReportsPage />} />
          <Route path="tools" element={<ToolsPage />} />
          <Route path="whatsapp-templates" element={<MessageTemplatesPage />} />
          <Route path="whatsapp-logs" element={<WhatsAppLogsPage />} />
          <Route path="routines" element={<OperationalRoutinesPage />} />
          <Route path="coin-extract" element={<CoinExtractPage />} />
          <Route path="po-shortage" element={<POShortageDashboardPage />} />
          <Route path="technicians-status" element={<TechniciansStatusPage />} />
          <Route path="os-analytics" element={<OSAnalyticsDrilldownPage />} />
          <Route path="*" element={<Navigate to="dashboard" replace />} />
        </Route>
      </Routes>
    </TenantProvider>
  );
}

function TenantRedirect() {
  const { user, tenantSlug, loading } = useAuth();
  if (loading) return null;
  if (user && tenantSlug) {
    return <Navigate to={`/t/${tenantSlug}/dashboard`} replace />;
  }
  return <Navigate to="/login" replace />;
}

function AppRouter() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/supplier-portal/:token" element={<SupplierPortalPage />} />
      <Route path="/po-confirmation/:token" element={<POConfirmationPage />} />

      {/* Platform Admin */}
      <Route path="/admin/*" element={<ProtectedRoute><Suspense fallback={null}><PlatformAdminPage /></Suspense></ProtectedRoute>} />

      {/* Tenant routes */}
      <Route path="/t/:slug/*" element={<TenantRoutes />} />

      {/* Legacy routes — redirect to tenant path */}
      <Route path="/dashboard" element={<TenantRedirect />} />
      <Route path="/" element={<TenantRedirect />} />
      <Route path="*" element={<TenantRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
